import styled from 'styled-components';
import { motion } from 'framer-motion';

export const BRAND = '#18382F';
export const BRAND_TINT = '#F3EEE3';
export const BRAND_DARK = '#24493E';
export const BRAND_BORDER = '#E0D3B5';
export const TEXT_PRIMARY = '#1F2933';
export const TEXT_MUTED = '#5F6B76';

export const Container = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 2rem 0;
  box-sizing: border-box;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${TEXT_PRIMARY};

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 2rem 1rem;
  }
`;

export const PageStack = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Card = styled.section`
  background: #fff;
  border: 0.5px solid ${BRAND_BORDER};
  border-radius: 12px;
  padding: 1.25rem;
  box-sizing: border-box;
`;

export const Hero = styled(Card)`
  text-align: center;
`;

export const HeroIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: ${BRAND_TINT};
  color: ${BRAND_DARK};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Headline = styled.h1`
  margin: 0;
  font-size: 20px;
  line-height: 1.35;
  font-weight: 500;
  color: ${TEXT_PRIMARY};
`;

export const HeroSubtitle = styled.p`
  margin: 0.5rem 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${TEXT_MUTED};
  overflow-wrap: anywhere;
`;

export const EmailNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${BRAND_TINT};
  border-radius: 8px;
  padding: 10px 14px;
  color: ${TEXT_MUTED};
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;

  svg {
    color: ${BRAND};
    flex-shrink: 0;
  }

  strong {
    color: ${BRAND_DARK};
    font-weight: 700;
  }
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  width: fit-content;
  background: ${BRAND_TINT};
  color: ${BRAND_DARK};
  border: 0.5px solid ${BRAND_BORDER};
  border-radius: 999px;
  padding: 0.35rem 0.625rem;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;

  svg {
    color: ${BRAND};
  }
`;

export const WhatsAppCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 500;
  color: ${TEXT_PRIMARY};
`;

export const MutedText = styled.p`
  margin: 0;
  color: ${TEXT_MUTED};
  font-size: 14px;
  line-height: 1.55;
`;

export const WhatsAppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CourseJoinRow = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: ${BRAND_TINT};
  border-radius: 8px;
  padding: 0.75rem;
  text-decoration: none;
  color: ${TEXT_PRIMARY};

  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }

  @media (max-width: 360px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const CourseJoinName = styled.span`
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const JoinButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 32px;
  padding: 0 0.875rem;
  border-radius: 20px;
  background: ${BRAND};
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.2s ease;

  ${CourseJoinRow}:hover & {
    background: ${BRAND_DARK};
  }
`;

export const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.875rem;
  color: ${TEXT_MUTED};
  font-size: 11px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  svg {
    color: ${BRAND};
  }
`;

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

export const MetadataTile = styled.div`
  background: ${BRAND_TINT};
  border-radius: 8px;
  padding: 0.75rem;
  min-width: 0;
`;

export const TileLabel = styled.div`
  margin-bottom: 0.35rem;
  color: ${BRAND};
  font-size: 11px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const TileValue = styled.div`
  color: ${BRAND_DARK};
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
  overflow-wrap: anywhere;
`;

export const PromoPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  background: ${BRAND_TINT};
  color: ${BRAND_DARK};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  overflow-wrap: anywhere;

  svg {
    color: ${BRAND};
    flex-shrink: 0;
  }
`;

export const LineItems = styled.div`
  display: flex;
  flex-direction: column;
`;

export const LineRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 0.5px solid ${BRAND_BORDER};
  font-size: 14px;
  line-height: 1.45;

  &:last-child {
    border-bottom: none;
  }
`;

export const LineLabel = styled.span`
  min-width: 0;
  color: ${TEXT_PRIMARY};
  overflow-wrap: anywhere;
`;

export const ItemDetails = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const ItemName = styled.span`
  color: ${TEXT_PRIMARY};
  font-size: 14px;
  line-height: 1.45;
`;

export const ItemMeta = styled.span`
  color: ${TEXT_MUTED};
  font-size: 12px;
  line-height: 1.4;
`;

export const LineValue = styled.span`
  flex-shrink: 0;
  color: ${props => props.$accent ? BRAND : TEXT_PRIMARY};
  font-weight: 500;
  text-align: right;
`;

export const TotalRow = styled(LineRow)`
  align-items: center;
  padding-bottom: 0;
  border-bottom: none;
`;

export const TotalValue = styled.span`
  color: ${BRAND};
  font-size: 18px;
  font-weight: 500;
  text-align: right;
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 13px 1rem;
  border-radius: 8px;
  border: 0.5px solid ${props => props.$primary ? BRAND : BRAND_BORDER};
  background: ${props => props.$primary ? BRAND : 'transparent'};
  color: ${props => props.$primary ? '#fff' : BRAND_DARK};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? BRAND_DARK : BRAND_TINT};
    border-color: ${props => props.$primary ? BRAND_DARK : BRAND_BORDER};
  }

  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
`;
